/**
 * Formatteurs purs de la console tuteur (Sprint 16, PRD Module 10).
 *
 * RÈGLE QUI GOUVERNE CE FICHIER : les TEXTES de la console — motifs, ancrages,
 * titres d'écartés, critères, libellés de bouton, `effetVisible` — sont rédigés
 * par le serveur (`packages/api/src/modules/tutor/tutor.format.ts`) et affichés
 * TELS QUELS. On ne trouve donc ici que ce que le contrat laisse au client :
 * des libellés d'énumération, une échelle de position et du formatage de date.
 * Rien qui reformule une phrase du serveur.
 *
 * Invariant produit tenu ici : aucune fonction ne produit de pourcentage, de
 * moyenne ni de niveau agrégé. `positionPalier` renvoie un RANG sur cinq
 * (1…5), destiné à une échelle de segments toujours accompagnée du libellé —
 * jamais un score, jamais additionnable.
 */
import {
  denombrer,
  phraseDerniereSeance,
  LIBELLES_NIVEAU_MAITRISE,
  NIVEAUX_MAITRISE,
  type AideDevoirsStatut,
  type Matiere,
  type MotifEcart,
  type NiveauMaitrise,
  type NiveauScolaire,
  type QualificatifConfiance,
  type TypeUniteTravail,
} from '@silex/shared';

/* ------------------------------------------------------------- paliers */

/** Première lettre en capitale — « en consolidation » → « En consolidation ». */
function capitaliser(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Libellé du palier, à partir de la source unique du contrat
 * (`LIBELLES_NIVEAU_MAITRISE`), capitalisé pour un usage en étiquette.
 */
export function libellePalier(niveau: NiveauMaitrise): string {
  return capitaliser(LIBELLES_NIVEAU_MAITRISE[niveau]);
}

/**
 * Rang du palier sur l'échelle des cinq, de 1 (`non_evalue`) à 5 (`maitrise`).
 * Sert UNIQUEMENT à allumer n segments d'une échelle de position rendue à côté
 * du libellé. Ce n'est pas une note : on ne l'affiche jamais en chiffre, on ne
 * l'additionne jamais, on n'en fait jamais de moyenne.
 */
export function positionPalier(niveau: NiveauMaitrise): number {
  return NIVEAUX_MAITRISE.indexOf(niveau) + 1;
}

/** Nombre total de paliers — la longueur de l'échelle affichée. */
export const NB_PALIERS = NIVEAUX_MAITRISE.length;

/**
 * Le panier « à surveiller » du critère de recommandation : `non_evalue` et
 * `en_decouverte`. C'est lui, et lui seul, qui prend la teinte d'attention.
 */
export function estASurveiller(niveau: NiveauMaitrise): boolean {
  return niveau === 'non_evalue' || niveau === 'en_decouverte';
}

/* ------------------------------------------------------- énumérations */

const LIBELLES_TYPE_UNITE: Readonly<Record<TypeUniteTravail, string>> = {
  signal_securite: 'Signal de sécurité',
  evaluation_a_valider: 'Évaluation à valider',
  eleve_a_relancer: 'Élève à relancer',
  blocage_savoir: 'Blocage à traiter',
};

/** Le type d'unité écrit en toutes lettres — jamais porté par la couleur seule. */
export function libelleTypeUnite(type: TypeUniteTravail): string {
  return LIBELLES_TYPE_UNITE[type];
}

const LIBELLES_NIVEAU_SCOLAIRE: Readonly<Record<NiveauScolaire, string>> = {
  sec1: 'sec. 1',
  sec2: 'sec. 2',
  sec3: 'sec. 3',
  sec4: 'sec. 4',
  sec5: 'sec. 5',
};

export function libelleNiveauScolaire(niveau: NiveauScolaire): string {
  return LIBELLES_NIVEAU_SCOLAIRE[niveau] ?? niveau;
}

const MATIERES_CONSOLE: Readonly<Record<Matiere, string>> = {
  francais: 'français',
  mathematiques: 'mathématiques',
};

/**
 * La matière dans une ligne de contexte (« sec. 2 · mathématiques ») : en bas
 * de casse, parce qu'elle suit un séparateur au milieu d'une phrase de repère.
 * `matiereLabel` (suivi-format) reste la forme capitalisée, en tête de ligne.
 */
export function matiereConsole(matiere: Matiere): string {
  return MATIERES_CONSOLE[matiere] ?? matiere;
}

const LIBELLES_CONFIANCE: Readonly<Record<QualificatifConfiance, string>> = {
  faible: 'Confiance faible',
  moyenne: 'Confiance moyenne',
  elevee: 'Confiance élevée',
};

/**
 * La confiance du moteur est qualifiée en mots, jamais en nombre : un tuteur ne
 * décide pas sur « 0,62 » (cf. `QUALIFICATIFS_CONFIANCE`).
 */
export function libelleConfiance(q: QualificatifConfiance): string {
  return LIBELLES_CONFIANCE[q];
}

const LIBELLES_STATUT_AIDE: Readonly<Record<AideDevoirsStatut, string>> = {
  en_cours: 'En cours',
  termine: 'Terminée',
  abandonne: 'Abandonnée',
};

export function libelleStatutAide(statut: AideDevoirsStatut): string {
  return LIBELLES_STATUT_AIDE[statut] ?? statut;
}

/**
 * Un écarté « avance » n'appelle aucune attention ; les trois autres motifs non
 * plus, mais ils se lisent différemment. Aucun n'est un rouge : le seul rouge
 * de la console est le signal de détresse.
 */
export function estEcartInactif(motif: MotifEcart): boolean {
  return motif === 'inactivite_sous_seuil' || motif === 'jamais_actif';
}

/* ------------------------------------------------------------- durées */

/** « 2 min ». La durée annoncée est ce qui rend la file crédible. */
export function formatDuree(minutes: number): string {
  return `${minutes} min`;
}

/**
 * « Environ 7 minutes au total » — l'annonce en tête de file de validation.
 * Au singulier sous la minute pleine, comme le veut le français.
 */
export function formatDureeTotale(minutes: number): string {
  return minutes <= 1 ? 'Environ 1 minute au total' : `Environ ${minutes} minutes au total`;
}

/* -------------------------------------------------------------- dates */

/**
 * Fuseau de lecture de la console : Neth Académie est au Québec, les
 * horodatages sont stockés en UTC. Même choix que `tutor.format.ts` côté API
 * (`America/Toronto` : mêmes règles d'heure avancée que Montréal).
 */
export const FUSEAU_CONSOLE = 'America/Toronto';

const FMT_DATE = new Intl.DateTimeFormat('fr-CA', {
  timeZone: FUSEAU_CONSOLE,
  day: 'numeric',
  month: 'long',
});

const FMT_JOUR_DATE = new Intl.DateTimeFormat('fr-CA', {
  timeZone: FUSEAU_CONSOLE,
  weekday: 'long',
  day: 'numeric',
  month: 'long',
});

const FMT_HEURE = new Intl.DateTimeFormat('fr-CA', {
  timeZone: FUSEAU_CONSOLE,
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/** « 30 août ». */
export function formatDate(iso: string): string {
  return FMT_DATE.format(new Date(iso));
}

/** « lundi 31 août ». */
export function formatJourDate(iso: string): string {
  return capitaliser(FMT_JOUR_DATE.format(new Date(iso)));
}

/** « 30 août, 17 h 40 » — registre télégraphique d'une ligne de données. */
export function formatDateHeure(iso: string): string {
  return `${FMT_DATE.format(new Date(iso))}, ${FMT_HEURE.format(new Date(iso))}`;
}

/**
 * LOT 1.6 — la règle de cette phrase a quitté la console pour
 * `@silex/shared/editorial` : le mobile du tuteur dit le même fait, et deux
 * copies d'une phrase finissent toujours par ne plus dire la même chose. Le
 * nom local est conservé pour ne pas réécrire ses points d'appel. (Au passage,
 * la copie d'ici écrivait « depuis 1 jours » : la règle partagée porte l'accord.)
 */
export const formatJoursSansSeance = phraseDerniereSeance;

/* ------------------------------------------------------------- divers */

/** Initiale d'un prénom pour l'avatar carré des listes. */
export function initiale(prenom: string): string {
  return prenom.trim().charAt(0).toUpperCase() || '·';
}

/**
 * « 3 savoirs » / « 1 savoir » / « 0 savoir » — accord simple, aucun calcul de
 * moyenne.
 *
 * LOT 3.2 — la console portait ici SA copie de la règle de nombre, le mobile
 * en portait d'autres, et l'une d'elles appliquait la règle anglaise
 * (`MOB-TUT-JOU-10` : « 0 élèveS suiviS »). La règle vit désormais une seule
 * fois, dans `@silex/shared` ; cette fonction n'est plus qu'un alias conservé
 * pour ne pas réécrire ses quarante points d'appel.
 */
export const pluriel = denombrer;
